import { useEffect, useState } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { SCENE_IDS } from "./sceneAssets";

gsap.registerPlugin(ScrollTrigger);

/**
 * Single reusable timeline — fade-only crossfades keep images sharp on all viewports.
 */
export function useCinematicTimeline({
  enabled,
  rootRef,
  pinRef,
  sceneRefs,
  totalVh,
  slowScroll = false,
}) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!enabled) return undefined;
    const id = requestAnimationFrame(() => setReady(true));
    return () => cancelAnimationFrame(id);
  }, [enabled]);

  useEffect(() => {
    if (!enabled || !ready || !rootRef.current || !pinRef.current) return undefined;

    const root = rootRef.current;
    const pin = pinRef.current;
    const scenes = SCENE_IDS.map((id) => sceneRefs.current[id]).filter(Boolean);
    if (scenes.length < SCENE_IDS.length) return undefined;

    const scrollDistance = () => window.innerHeight * (totalVh / 100);

    const ctx = gsap.context(() => {
      scenes.forEach((scene, index) => {
        const media = scene.querySelector(".cinematic-scene__media");
        const copy = scene.querySelector(".cinematic-scene__copy");

        gsap.set(scene, {
          autoAlpha: index === 0 ? 1 : 0,
          zIndex: index === 0 ? 2 : 1,
        });
        gsap.set(media, { clearProps: "transform,filter" });
        gsap.set(copy, {
          autoAlpha: index === 0 ? 1 : 0,
          y: index === 0 ? 0 : 16,
        });
      });

      const tl = gsap.timeline({
        defaults: { ease: "none" },
        scrollTrigger: {
          trigger: root,
          start: "top top",
          end: () => `+=${scrollDistance()}`,
          pin,
          scrub: slowScroll ? 0.75 : 0.9,
          anticipatePin: 1,
          invalidateOnRefresh: true,
        },
      });

      const seg = 1;

      SCENE_IDS.slice(0, -1).forEach((_, i) => {
        const from = scenes[i];
        const to = scenes[i + 1];
        const fromCopy = from.querySelector(".cinematic-scene__copy");
        const toCopy = to.querySelector(".cinematic-scene__copy");
        const at = i * seg;

        tl.to(from, { autoAlpha: 0, zIndex: 1, duration: seg * 0.5 }, at + seg * 0.35);
        tl.to(fromCopy, { autoAlpha: 0, y: -12, duration: seg * 0.35 }, at + seg * 0.2);
        tl.set(to, { zIndex: 3 }, at + seg * 0.15);
        tl.fromTo(
          to,
          { autoAlpha: 0 },
          { autoAlpha: 1, duration: seg * 0.5 },
          at + seg * 0.25
        );
        tl.fromTo(
          toCopy,
          { autoAlpha: 0, y: 20 },
          { autoAlpha: 1, y: 0, duration: seg * 0.4 },
          at + seg * 0.35
        );
      });
    }, root);

    requestAnimationFrame(() => ScrollTrigger.refresh());

    return () => ctx.revert();
  }, [enabled, ready, rootRef, pinRef, sceneRefs, totalVh, slowScroll]);
}
